/**
 * Admin overview.
 *
 * Summary before detail: the KPI row answers "is anything wrong" before the panels
 * answer "with what". Figures that cannot be computed yet render "—" rather than 0,
 * because a real zero and an unavailable number mean different things to someone
 * deciding whether to act.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Banner, Card, Icon, Kpi, KpiRow } from '../../shared/ui';
import { getConfig } from '../../shared/lib/config';
import { openedx } from '../../shared/lib/openedx';
import { PageHeader } from '../AdminShell';

interface CourseList {
  results: { id: string; name: string; org: string }[];
  pagination?: { count: number };
}

export default function AdminHome() {
  const config = getConfig();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-courses-summary'],
    queryFn: () => openedx.get<CourseList>('/api/courses/v1/courses/?page_size=5'),
    // Not retried on purpose: this read fails most often when the platform is already
    // over capacity, and a retry would add to the load that caused it. The banner below
    // says so, and a reload is the administrator's decision.
    retry: false,
  });

  const courses = data?.results ?? [];
  // A FAILED READ IS "UNAVAILABLE", NOT ZERO — the distinction this file's header opens with.
  // Without the `isError` arm, a rejected request leaves `courses` empty with `isLoading`
  // false, so this resolved to 0 and the KPI row asserted "Courses: 0" about an academy whose
  // catalogue simply had not answered.
  const courseCount = isError
    ? undefined
    : (data?.pagination?.count ?? (isLoading ? undefined : courses.length));

  return (
    <>
      <PageHeader
        title={`${config.tenant.name} admin`}
        description="Courses, people and engagement for this academy."
      />

      <KpiRow>
        <Kpi label="Courses" value={courseCount ?? '—'} />
        <Kpi label="Learners" value="—" hint="Needs the enrolment report" />
        <Kpi label="Team" value="—" hint="Needs the roles report" />
        <Kpi label="Organisations" value={config.tenant.orgs.length} hint={config.tenant.orgs.join(', ')} />
      </KpiRow>

      <div className="admin__cols">
        <Card
          title="Recent courses"
          action={
            <Link to="/admin/courses" className="ui-btn" data-variant="ghost">
              View all
            </Link>
          }
        >
          {/* The error AND loading arms both precede the empty arm, because "no courses in
              this academy yet" is a claim about the academy and may only be made once it has
              answered. The KPI above already draws this distinction — `isLoading` resolves
              the count to `undefined`, not 0 — and it would be undone here if this card said
              the academy was empty while that same read was still in flight. */}
          {isLoading ? (
            <p style={{ color: 'var(--ink-muted)', margin: 0 }}>Loading courses…</p>
          ) : isError ? (
            <Banner tone="danger">
              <strong>Could not load courses.</strong> The academy course list did not respond.
              Nothing has changed — reload to try again, and check that the LMS is running.
            </Banner>
          ) : courses.length === 0 ? (
            <p style={{ color: 'var(--ink-muted)', margin: 0 }}>
              No courses in {config.tenant.orgs.join(', ') || 'this academy'} yet.
            </p>
          ) : (
            <ul className="admin__list">
              {courses.map((course) => (
                <li key={course.id}>
                  <Link to={`/admin/courses/${encodeURIComponent(course.id)}`}>{course.name}</Link>
                  <span>{course.org}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Shortcuts">
          <div className="admin__shortcuts">
            <Link to="/admin/courses">
              <Icon name="book_2" />
              Courses
            </Link>
            <Link to="/admin/learners">
              <Icon name="group" />
              Learners
            </Link>
            <Link to="/admin/enrolments">
              <Icon name="how_to_reg" />
              Enrolments
            </Link>
            <Link to="/admin/paths">
              <Icon name="route" />
              Learning Paths
            </Link>
          </div>
        </Card>
      </div>
    </>
  );
}
